const ShippingProtection = {
  toggleSelector: '#ShippingProtectionToggle',
  wrapperSelector: '.shipping-protection',
  optedOutStorageKey: 'shippingProtectionOptedOut',
  isBusy: false,

  get root() {
    return window.Shopify?.routes?.root || '/';
  },

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  },

  getWrapper() {
    return document.querySelector(this.wrapperSelector);
  },

  getToggle() {
    return document.querySelector(this.toggleSelector);
  },

  getVariantId() {
    const wrapper = this.getWrapper();
    if (!wrapper) return null;

    const id = Number(wrapper.dataset.protectionVariantId);
    return Number.isFinite(id) ? id : null;
  },

  isOptedOut() {
    try {
      return localStorage.getItem(this.optedOutStorageKey) === 'true';
    } catch (error) {
      return false;
    }
  },

  setOptedOut(value) {
    try {
      if (value) {
        localStorage.setItem(this.optedOutStorageKey, 'true');
      } else {
        localStorage.removeItem(this.optedOutStorageKey);
      }
    } catch (error) {
      // Ignore storage errors
    }
  },

  async getCart() {
    const response = await fetch(`${this.root}cart.js`, {
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }

    return response.json();
  },

  findProtectionLine(cart, variantId) {
    return cart.items.find((item) => Number(item.variant_id) === Number(variantId));
  },

  getRegularItems(cart, variantId) {
    return cart.items.filter((item) => Number(item.variant_id) !== Number(variantId));
  },

  hasRegularProducts(cart, variantId) {
    return this.getRegularItems(cart, variantId).length > 0;
  },

  async addProtection(variantId) {
    const response = await fetch(`${this.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              _shipping_protection: 'true',
            },
          },
        ],
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to add protection');
    }

    return response.json();
  },

  async removeProtection(lineKey) {
    const response = await fetch(`${this.root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to remove protection');
    }

    return response.json();
  },

  isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  },

  getInfoTrigger() {
    return document.querySelector('[data-shipping-protection-info-trigger]');
  },

  getPopover() {
    return document.querySelector('[data-shipping-protection-popover]');
  },

  openInfo() {
    const trigger = this.getInfoTrigger();
    const popover = this.getPopover();

    if (!trigger || !popover) return;

    trigger.setAttribute('aria-expanded', 'true');
    popover.setAttribute('aria-hidden', 'false');
    popover.classList.add('is-open');
  },

  closeInfo() {
    const trigger = this.getInfoTrigger();
    const popover = this.getPopover();

    if (!trigger || !popover) return;

    trigger.setAttribute('aria-expanded', 'false');
    popover.setAttribute('aria-hidden', 'true');
    popover.classList.remove('is-open');
  },

  renderSections(parsedState) {
    const cartDrawer = this.cartDrawer;

    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents(parsedState);
      return;
    }

    document.dispatchEvent(new CustomEvent('cart:refresh'));
  },

  async sync() {
    const toggle = this.getToggle();
    const variantId = this.getVariantId();

    if (!toggle || !variantId || this.isBusy) return;

    try {
      const cart = await this.getCart();
      const protectionLine = this.findProtectionLine(cart, variantId);
      const hasRegularProducts = this.hasRegularProducts(cart, variantId);
      const optedOut = this.isOptedOut();

      if (!hasRegularProducts) {
        this.setOptedOut(false);

        if (protectionLine) {
          this.isBusy = true;
          const result = await this.removeProtection(protectionLine.key);
          this.renderSections(result);
          return;
        }

        toggle.checked = false;
        toggle.disabled = false;

        if (this.cartDrawer && typeof this.cartDrawer.scheduleSyncEmptyState === 'function') {
          this.cartDrawer.scheduleSyncEmptyState();
        }

        return;
      }

      if (protectionLine) {
        toggle.checked = true;
        toggle.disabled = false;
        return;
      }

      if (optedOut) {
        toggle.checked = false;
        toggle.disabled = false;
        return;
      }

      toggle.checked = true;
      toggle.disabled = true;

      this.isBusy = true;
      const result = await this.addProtection(variantId);
      this.renderSections(result);
    } catch (error) {
      console.error('[Shipping Protection] Sync failed:', error);

      const freshToggle = this.getToggle();
      if (freshToggle) {
        freshToggle.disabled = false;
      }
    } finally {
      this.isBusy = false;
      const wrapper = this.getWrapper();
      if (wrapper) wrapper.classList.remove('loading');
    }
  },

  bindEvents() {
    if (this._bound) return;
    this._bound = true;

    document.addEventListener('change', async (event) => {
      const toggle = event.target.closest(this.toggleSelector);
      if (!toggle || this.isBusy) return;

      const variantId = this.getVariantId();
      if (!variantId) return;

      this.isBusy = true;

      const wrapper = this.getWrapper();
      if (wrapper) wrapper.classList.add('loading');

      toggle.disabled = true;

      try {
        const cart = await this.getCart();
        const protectionLine = this.findProtectionLine(cart, variantId);
        const hasRegularProducts = this.hasRegularProducts(cart, variantId);

        if (toggle.checked) {
          this.setOptedOut(false);

          if (!hasRegularProducts) {
            toggle.checked = false;
            toggle.disabled = false;
            return;
          }

          if (!protectionLine) {
            const result = await this.addProtection(variantId);
            this.renderSections(result);
            return;
          }
        } else {
          this.setOptedOut(true);

          if (protectionLine) {
            const result = await this.removeProtection(protectionLine.key);
            this.renderSections(result);
            return;
          }
        }

        toggle.disabled = false;

        if (this.cartDrawer && typeof this.cartDrawer.scheduleSyncEmptyState === 'function') {
          this.cartDrawer.scheduleSyncEmptyState();
        }
      } catch (error) {
        console.error('[Shipping Protection] Update failed:', error);

        const freshToggle = this.getToggle();
        if (freshToggle) {
          freshToggle.disabled = false;
        }
      } finally {
        this.isBusy = false;

        const freshWrapper = this.getWrapper();
        if (freshWrapper) freshWrapper.classList.remove('loading');
      }
    });

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-shipping-protection-info-trigger]');
      const closeButton = event.target.closest('[data-shipping-protection-info-close]');
      const popover = this.getPopover();

      if (trigger) {
        event.preventDefault();
        event.stopPropagation();

        if (this.isMobileViewport()) {
          const isOpen = popover && popover.classList.contains('is-open');
          if (isOpen) {
            this.closeInfo();
          } else {
            this.openInfo();
          }
        }
        return;
      }

      if (closeButton) {
        event.preventDefault();
        this.closeInfo();
        return;
      }

      if (popover && popover.classList.contains('is-open')) {
        const clickedInsidePopover = event.target.closest('[data-shipping-protection-popover]');
        if (!clickedInsidePopover) {
          this.closeInfo();
        }
      }
    });

    document.addEventListener('mouseover', (event) => {
      if (this.isMobileViewport()) return;

      const trigger = event.target.closest('[data-shipping-protection-info-trigger]');
      if (trigger) {
        this.openInfo();
      }
    });

    document.addEventListener('mouseout', (event) => {
      if (this.isMobileViewport()) return;

      const wrapper = this.getWrapper();
      if (!wrapper) return;

      const relatedTarget = event.relatedTarget;
      if (wrapper.contains(event.target) && !wrapper.contains(relatedTarget)) {
        this.closeInfo();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeInfo();
      }
    });

    document.addEventListener('focusin', (event) => {
      if (!this.isMobileViewport() && event.target.closest('[data-shipping-protection-info-trigger]')) {
        this.openInfo();
      }
    });

    document.addEventListener('focusout', (event) => {
      if (this.isMobileViewport()) return;

      const wrapper = this.getWrapper();
      if (!wrapper) return;

      requestAnimationFrame(() => {
        if (!wrapper.contains(document.activeElement)) {
          this.closeInfo();
        }
      });
    });
  },
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._syncEmptyStateRaf = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
    this.setupEmptyStateObserver();
  }

  connectedCallback() {
    this.scheduleSyncEmptyState();
  }

  setupEmptyStateObserver() {
    if (this._emptyStateObserver) return;

    this._emptyStateObserver = new MutationObserver(() => {
      this.scheduleSyncEmptyState();
    });

    this._emptyStateObserver.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  scheduleSyncEmptyState() {
    if (this._syncEmptyStateRaf) {
      cancelAnimationFrame(this._syncEmptyStateRaf);
    }

    this._syncEmptyStateRaf = requestAnimationFrame(() => {
      this._syncEmptyStateRaf = null;
      this.syncEmptyState();
    });
  }

  syncEmptyState() {
    const drawerInner = this.querySelector('.drawer__inner');
    if (!drawerInner) return;

    const visibleCartItems = this.querySelectorAll('#CartDrawer-CartItems tbody .cart-item');
    const hasVisibleItems = visibleCartItems.length > 0;

    drawerInner.classList.toggle('is-empty', !hasVisibleItems);
    this.classList.toggle('is-empty', !hasVisibleItems);

    const cartDrawerItems = this.querySelector('cart-drawer-items');
    if (cartDrawerItems) {
      cartDrawerItems.classList.toggle('is-empty', !hasVisibleItems);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
      const shopifyChat = document.getElementById('shopify-chat');
      if (shopifyChat) shopifyChat.style.zIndex = '-1';
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    this.scheduleSyncEmptyState();

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        if (containerToTrapFocusOn) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.hideShopifyChat();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.showShopifyChat();
  }

  hideShopifyChat() {
    clearTimeout(this._chatTimer);
    this._chatTimer = setTimeout(() => {
      const chat = document.getElementById('shopify-chat');
      if (chat) chat.style.setProperty('transform', 'translateY(200%)', 'important');
    }, 50);
  }

  showShopifyChat() {
    clearTimeout(this._chatTimer);
    const chat = document.getElementById('shopify-chat');
    if (chat) chat.style.removeProperty('transform');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    document.dispatchEvent(
      new CustomEvent('recently-viewed:refresh', {
        detail: { source: 'cart-drawer-render' },
      })
    );

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.scheduleSyncEmptyState();
    this.open();

    setTimeout(() => {
      if (window.ShippingProtection) {
        window.ShippingProtection.sync();
      }
      this.scheduleSyncEmptyState();
    }, 0);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);
    if (element) return element.innerHTML;
    return doc.body ? doc.body.innerHTML : html;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

window.ShippingProtection = ShippingProtection;

ShippingProtection.bindEvents();

document.addEventListener('DOMContentLoaded', () => {
  ShippingProtection.sync();

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.scheduleSyncEmptyState === 'function') {
    cartDrawer.scheduleSyncEmptyState();
  }

  document.documentElement.classList.remove('cart-drawer-loading');
});